import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Text } from 'components/Text';
import PrimaryButton from 'components/Button/PrimaryButton';
import logo from 'graphics/logo.svg';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Routes from 'lib/utils/routes';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
`;

const ContentBox = styled.div`
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  text-align: center;
  flex-direction: column;
  display: flex;
`;

const LogoImage = styled.img`
  max-width: 300px;
  height: auto;
  margin: 0 auto 2rem auto;
`;

const Pwa = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installable, setInstallable] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [supportsPwa, setSupportsPwa] = useState(true);
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    const standalone =
      (window.matchMedia &&
        window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true;
    setIsStandalone(!!standalone);
    // Detect iOS Safari or macOS Safari (which don't support beforeinstallprompt)
    const isSafari = /^((?!chrome|android).)*safari/i.test(
      window.navigator.userAgent
    );
    const isIosDevice = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    setIsIos(isSafari || isIosDevice);
    // Check if browser supports PWAs (service workers are a good proxy)
    const hasPwaSupport = 'onbeforeinstallprompt' in window;
    setSupportsPwa(hasPwaSupport);

    function onBeforeInstallPrompt(e) {
      if (standalone) return;
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallable(true);
    }

    function onAppInstalled() {
      setInstallable(false);
      setDeferredPrompt(null);
      setIsStandalone(true);
    }

    // Listen for display mode changes to catch when app enters standalone mode
    let mediaQuery = null;
    let mediaQueryHandler = null;
    try {
      mediaQuery = window.matchMedia('(display-mode: standalone)');
      mediaQueryHandler = (e) => {
        if (e.matches && window.location.pathname === '/pwa') {
          window.location.replace('/');
        }
      };
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', mediaQueryHandler);
      }
    } catch {
      // Ignore errors if matchMedia is not supported in this browser
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
      if (mediaQuery && mediaQueryHandler) {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', mediaQueryHandler);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (isStandalone) {
      history.replace(Routes.rootPath());
    }
  }, [isStandalone, history]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } catch {
      // Ignore userChoice promise rejection (user cancelled or error)
    }
    setDeferredPrompt(null);
    setInstallable(false);
  }, [deferredPrompt]);

  return (
    <Container>
      <ContentBox>
        <LogoImage src={logo} alt="ShopPulse" />
        {installable ? (
          <>
            <Text>{t('pwa|install|body')}</Text>
            <PrimaryButton
              style={{ margin: '2rem auto 0 auto' }}
              onClick={handleInstall}
            >
              {t('pwa|install|button')}
            </PrimaryButton>
          </>
        ) : isIos ? (
          <>
            <Text>{t('pwa|install|body')}</Text>
            <Text style={{ marginTop: '2rem' }}>
              {t('pwa|install|iosHint')}
            </Text>
          </>
        ) : !supportsPwa ? (
          <>
            <Text>{t('pwa|install|notAvailable')}</Text>
            <Text style={{ marginTop: '2rem' }}>
              {t('pwa|install|browserHint')}
            </Text>
          </>
        ) : (
          <Text>{t('pwa|install|alreadyInstalled')}</Text>
        )}
      </ContentBox>
    </Container>
  );
};

export default Pwa;
